import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const INITIAL_MESSAGE = {
  type: 'ai' as const,
  content: "Olá! Eu sou o waffle chat 🧇, seu assistente para métricas e análises.\n\nPosso ajudar com:\n• Informações sobre newsletters e assinantes\n• Métricas de ads (Meta, Google, TikTok)\n• Performance de campanhas\n• Análise de crescimento\n\nComo posso te ajudar hoje?"
};

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{type: 'user' | 'ai', content: string}>>([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatMessageContent = (content: string) => {
    // Primeiro, vamos proteger os underscores que são parte de palavras técnicas
    const protectedContent = content.replace(/\b_[a-zA-Z0-9]+_\b/g, match => {
      return match.replace(/_/g, '§UNDERSCORE§');
    });
    
    return protectedContent
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Negrito
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Itálico
      .split('\n').join('<br />') // Quebras de linha
      .replace(/§UNDERSCORE§/g, '_'); // Restaura os underscores protegidos
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setInputValue('');
    setIsLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      const response = await fetch('https://waffle-v2.thenewcc.workers.dev/insights/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ message: userMessage })
      });

      if (!response.ok) {
        throw new Error(
          response.status === 401 
            ? 'Sessão expirada. Por favor, faça login novamente.' 
            : 'Erro ao processar sua mensagem. Tente novamente.'
        );
      }

      const data = await response.json();
      
      if (!data.response || typeof data.response !== 'string') {
        throw new Error('Desculpe, ainda não estou preparado para esse tipo de pergunta.');
      }

      setMessages(prev => [...prev, { type: 'ai', content: data.response }]);
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Ocorreu um erro inesperado. Por favor, tente novamente.';

      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: errorMessage
      }]);

      // If it's an authentication error, clear the user session
      if (errorMessage.includes('Sessão expirada')) {
        localStorage.removeItem('user');
        // Optionally, you could emit an event or call a function to trigger a re-login
        window.location.reload();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full bg-[#FF3366] text-white flex items-center justify-center shadow-lg hover:bg-[#FF3366]/90 transition-colors ${isOpen ? 'rotate-90' : ''}`}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-96 max-h-[80vh] bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col" style={{ maxHeight: 'calc(100vh - 100px)' }}>
          {/* Header */}
          <div className="p-4 border-b bg-[#FF3366] text-white rounded-t-lg flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                🧇
              </div>
              <div>
                <h3 className="font-medium">waffle chat</h3>
                <p className="text-sm text-white/80">Assistente de Métricas</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start items-start'}`}
                >
                  {message.type === 'ai' && (
                    <div className="w-6 h-6 rounded-full bg-[#FF3366]/10 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
                      🧇
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-[#FF3366] text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                    dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }}
                  />
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start items-start">
                  <div className="w-6 h-6 rounded-full bg-[#FF3366]/10 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
                    🧇
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t mt-auto flex-shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF3366] focus:border-transparent"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !inputValue.trim()}
                className="p-2 bg-[#FF3366] text-white rounded-md hover:bg-[#FF3366]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBubble;